<template>
  <div class="module-creator-item">
    <header class="module-creator-header">
      <h3 class="module-creator-title truncate ...">{{ title }}</h3>
      <IconsDecorLine class="h-1 w-18 text-primary max-lg:w-9 max-lg:h-0.5 rtl:-scale-x-100" />
    </header>
    <AspectRatio :ratio="16 / 9" class="overflow-hidden">
      <Swiper
        v-if="images.length > 1"
        :modules="[SwiperAutoplay, SwiperPagination]"
        :slides-per-view="1"
        :loop="true"
        :autoplay="{ delay: 5000, disableOnInteraction: false }"
        :pagination="paginationProps"
        class="relative"
      >
        <SwiperSlide v-for="image in images" :key="image">
          <NuxtImg :src="image" alt="" loading="lazy" format="webp" quality="80" />
        </SwiperSlide>
        <div class="swiper-pagination"></div>
      </Swiper>
      <NuxtImg v-else :src="images[0]" alt="" format="webp" quality="80" />
    </AspectRatio>
    <p class="module-creator-desc">
      {{ desc }}
    </p>
    <footer class="module-creator-footer">
      <NuxtLink v-if="url && !download" :to="url" :target="isSameOriginUrl(url) ? '_self' : '_blank'">
        <GfrButton class="module-creator-footer-btn">
          {{ name }}
          <IconsOutArrow class="ml-1 h-3.5 w-3.5 rtl:ml-0 rtl:mr-1 rtl:-rotate-90" />
        </GfrButton>
      </NuxtLink>
      <CommonDownload v-if="download && !config.is_ind" class="max-lg:hidden" />
    </footer>
  </div>
</template>

<script setup lang="ts">
import { Pagination as SwiperPagination } from 'swiper/modules'
import type { PaginationOptions } from 'swiper/types'
defineProps({
  images: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  title: String,
  desc: String,
  download: Boolean,
  url: String,
  name: String
})
defineEmits(['action'])
const { config } = storeToRefs(useStore())
const paginationProps = ref<PaginationOptions>({
  type: 'fraction',
  el: '.swiper-pagination',
  renderFraction: (currentClass: string, totalClass: string) => {
    return '<span class="' + currentClass + '">' + '</span>' + '/' + '<span class="' + totalClass + '">' + '</span>'
  },
  formatFractionCurrent: (number: number) => number.toString(),
  formatFractionTotal: (number: number) => number.toString()
})
</script>

<style lang="scss">
.swiper-pagination-current {
  @apply text-m-h4 text-primary max-lg:text-base;
}
</style>
<style scoped lang="scss">
.module-creator-item {
  @apply border border-dark-foreground bg-dark/60 px-6 py-4 max-lg:p-3;
}
.module-creator-header {
  @apply mb-4;
}
.module-creator-title {
  @apply mb-1 text-m-h3 font-medium max-lg:text-base max-lg:mb-0.5;
}
.module-creator-image {
  @apply h-[370px] w-full;
}
.module-creator-desc {
  @apply my-4 line-clamp-3 h-18 text-base font-medium text-light;
  @apply max-lg:my-2 max-lg:h-auto max-lg:line-clamp-none max-lg:text-xs max-lg:leading-4.5 max-lg:font-normal;
}
.module-creator-footer {
  @apply flex h-12 w-full items-center justify-end max-lg:portrait:h-auto max-lg:landscape:h-8;
}
.module-creator-footer-btn {
  @apply h-8 min-w-37 rounded-[2px] bg-primary-color px-2 py-1 text-m-p font-bold text-dark-foreground hover:bg-primary-hover;
  @apply max-lg:text-xs max-lg:leading-4.5;
}
.swiper-pagination {
  @apply left-auto right-0 -top-px z-10 inline-block h-10 w-16 pr-4.5 text-right align-baseline text-base font-medium leading-10;
  @apply max-lg:w-12.5 max-lg:h-8 max-lg:pr-3 max-lg:py-1 max-lg:text-sm;
  background: linear-gradient(to left, rgba(0, 0, 0, 1) 20%, rgba(0, 0, 0, 0));
}
</style>
